import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface Profile {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  website: string | null;
  location: string | null;
  is_private: boolean;
  created_at: string;
  posts_count: number;
  followers_count: number;
  following_count: number;
  pets_count: number;
}

interface Pet {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed: string | null;
  age: number | null;
  gender: string | null;
  description: string | null;
  avatar_url: string | null;
  posts_count: number;
  created_at: string;
  is_public: boolean;
}

interface Post {
  id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  pet_id: string | null;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileExists, setProfileExists] = useState<boolean | null>(null);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchPets();
      fetchPosts();
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      // Fetch profile with counts
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profileData) {
        setProfileExists(false);
        setLoading(false);
        return;
      }

      setProfileExists(true);

      // Get counts
      const [postsCount, petsCount, followersCount, followingCount] = await Promise.all([
        supabase.from('posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('pets').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('follows').select('id', { count: 'exact', head: true }).eq('following_id', user.id),
        supabase.from('follows').select('id', { count: 'exact', head: true }).eq('follower_id', user.id),
      ]);

      setProfile({
        ...profileData,
        posts_count: postsCount.count || 0,
        pets_count: petsCount.count || 0,
        followers_count: followersCount.count || 0,
        following_count: followingCount.count || 0,
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileExists(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchPets = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('pets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get post count for each pet
      const petsWithCounts = await Promise.all(
        (data || []).map(async (pet) => {
          const { count } = await supabase
            .from('posts')
            .select('id', { count: 'exact', head: true })
            .eq('pet_id', pet.id);
          
          return {
            ...pet,
            posts_count: count || 0,
            is_public: pet.is_public ?? true, // Ensure is_public exists
          };
        })
      );

      setPets(petsWithCounts);
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const fetchPosts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts((data as any) || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPetPosts = async (petId: string) => {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('pet_id', petId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as any) || [];
    } catch (error) {
      console.error('Error fetching pet posts:', error);
      return [];
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user || !profile) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setProfile({ ...profile, ...updates });
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error };
    }
  };

  return {
    profile,
    pets,
    posts,
    loading,
    profileExists,
    fetchPetPosts,
    updateProfile,
    refetchProfile: fetchProfile,
    refetchPets: fetchPets,
    refetchPosts: fetchPosts,
  };
};